import { Box, Tooltip, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuBookOutlined as TemplateIcon,
  Add as AddIcon,
  DeleteOutlineOutlined as DeleteIcon,
} from "@mui/icons-material";
import { v2, nums } from "./ui/v2Tokens";
import { macro } from "./ui/macroTokens";
import { TextButton } from "./ui/primitives";
import ConfirmDialog from "./dialogs/ConfirmDialog";
import { deleteMenuTemplate } from "../../../services/NutritionV2Service";
import { useMenuTemplatesV2 } from "./data/useNutritionV2";
import { listTotals, type MenuTemplateV2 } from "./model/nutritionV2Model";
import {
  POINTS,
  POINTS_SHORT,
  energyFigure,
  energyLabel,
  macroFigure,
  pointsFromMacro,
  type NutritionDisplayMode,
  type PointsMacroKey,
} from "./ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../contexts/NutritionDisplayContext";

const f1 = (n: number) => Math.round(n * 10) / 10;

/**
 * A table cell's bare figure — the column header carries the unit, so the cell does not. `–` for a
 * menu with none of it. In points mode the header says נקודות and every figure under it is one.
 */
const energyCell = (kcal: number, mode: NutritionDisplayMode): string =>
  kcal ? energyFigure(kcal, mode).text : "–";

const macroCell = (key: PointsMacroKey, grams: number, mode: NutritionDisplayMode): string =>
  (mode === POINTS ? pointsFromMacro(key, grams) : f1(grams)) ? macroFigure(key, grams, mode).text : "–";

/** The column headers in the company's unit: `קלוריות` → `נקודות`, and the macros say they are points. */
const headersFor = (headers: string[], mode: NutritionDisplayMode): string[] =>
  headers.map((h) =>
    h === "קלוריות" ? energyLabel(mode) : h === "מקרו" && mode === POINTS ? `מקרו (${POINTS_SHORT})` : h
  );

/**
 * Six columns against the ~281px a phone has inside this card. The wrapper scrolls, so nothing
 * breaks, but a trainer should not have to drag sideways to reach a template's delete button — so
 * below `md` the two derived counts and the macro split stand down and the name, the calories and
 * the action stay. `display: none` keeps the cells in the DOM in their original order.
 */
const MOBILE_HIDDEN = { display: { xs: "none", md: "table-cell" } };

/** Column indices of `HEADERS` that stand down on a phone: meals, items, macros. */
const HIDDEN_ON_MOBILE = new Set([1, 2, 4]);

const HEADERS = ["שם התבנית", "ארוחות", "פריטים", "קלוריות", "מקרו", ""];

const cell = {
  padding: { xs: "10px 8px", md: "12px 14px" },
  borderBottom: `1px solid ${v2.lineSoft}`,
  fontSize: "13px",
  color: v2.ink2,
  verticalAlign: "middle",
  textAlign: "center" as const,
  ...nums,
};

const th = {
  ...cell,
  padding: { xs: "8px 8px", md: "9px 14px" },
  fontSize: "11.5px",
  fontWeight: 500,
  color: v2.ink3,
  borderBottom: `1px solid ${v2.line}`,
  background: v2.canvas,
  whiteSpace: "nowrap" as const,
};

const iconBtn = (color: string) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: { xs: "40px", md: "30px" },
  height: { xs: "40px", md: "30px" },
  border: "none",
  background: "transparent",
  borderRadius: "6px",
  color: v2.ink3,
  cursor: "pointer",
  fontFamily: "inherit",
  padding: 0,
  transition: "background .12s, color .12s",
  "&:hover": { color, background: v2.lineSoft },
});

/**
 * The company's whole-menu templates.
 *
 * Deliberately the same screen as a trainee's menus list — the same table, the same row-click into a
 * builder, the same create button and the same delete. A template IS a menu without a trainee, and
 * the previous read-only card made it look like a different kind of object that could only be
 * produced as a by-product of building someone's menu. It could not be created, opened or corrected
 * here, so a typo in a template the whole company uses had no fix at all.
 *
 * These are the old global menu templates (`/app/global-templates`), which V20260902.1 copied into V2.
 */
const MenuTemplatesListV2 = () => {
  const navigate = useNavigate();
  const { data: templates, loading, error, reload } = useMenuTemplatesV2();
  const [deleting, setDeleting] = useState<MenuTemplateV2 | null>(null);
  const units = useNutritionDisplayMode();

  /** A template's totals are the sum of its meals — the same figure the builder shows. */
  const templateTotals = (template: MenuTemplateV2) =>
    listTotals(template.meals.flatMap((meal) => meal.items));

  const itemCount = (template: MenuTemplateV2) =>
    template.meals.reduce((sum, meal) => sum + meal.items.length, 0);

  return (
    <>
      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        title="למחוק את התבנית?"
        message={`"${deleting?.name}" תוסר מתבניות החברה ולא תהיה זמינה לאף מאמן. תפריטים שכבר נבנו ממנה לא ישתנו.`}
        confirmLabel="מחק תבנית"
        onConfirm={() => {
          if (deleting) void deleteMenuTemplate(deleting.id).then(reload);
          setDeleting(null);
        }}
      />

      <Box
        sx={{
          background: v2.surface,
          border: `1px solid ${v2.line}`,
          borderRadius: v2.rLg,
          boxShadow: v2.shadow,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // without this the title and its "{N} תבניות · זמינות לכל המאמנים בחברה" subtitle
            // crush the action button against the edge at 281px instead of wrapping above it
            flexWrap: "wrap",
            gap: "10px",
            padding: { xs: "12px 14px", md: "14px 18px" },
            borderBottom: `1px solid ${v2.line}`,
            background: v2.canvas,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: v2.surface,
                border: `1px solid ${v2.line}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TemplateIcon sx={{ color: v2.ink2, fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ fontSize: 15, fontWeight: 800, color: v2.ink }}>תבניות תפריטים</Box>
              <Box sx={{ fontSize: 11.5, color: v2.ink3, fontWeight: 600 }}>
                {templates.length} תבניות · זמינות לכל המאמנים בחברה
              </Box>
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => navigate("/app/menu-templates/new")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              background: v2.brand,
              color: "#fff",
              height: "36px",
              px: "14px",
              borderRadius: v2.rSm,
              fontSize: "13.5px",
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "background .12s",
              "&:hover": { background: v2.brandDark },
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            תבנית חדשה
          </Box>
        </Box>

        {/* Three states, not two — "still loading" and "has none" read identically if only the
            array length is checked, and a failed request would say "you have none" forever. */}
        {loading ? (
          <Box
            data-testid="templates-loading"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1.5,
              padding: "44px",
            }}
          >
            <CircularProgress size={26} sx={{ color: v2.brand }} />
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>טוען תבניות…</Box>
          </Box>
        ) : error ? (
          <Box
            data-testid="templates-error"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1,
              padding: "44px",
            }}
          >
            <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.over }}>לא הצלחנו לטעון את התבניות</Box>
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>בדוק את החיבור ונסה שוב</Box>
            <TextButton onClick={reload}>נסה שוב</TextButton>
          </Box>
        ) : templates.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 220,
              gap: 1,
              padding: "44px",
            }}
          >
            <TemplateIcon sx={{ fontSize: 46, color: v2.line }} />
            <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.ink3 }}>אין תבניות תפריטים</Box>
            <Box sx={{ fontSize: 13, color: v2.ink3, textAlign: "center" }}>
              בנה תבנית חדשה, או שמור תפריט קיים כתבנית מתוך רשימת התפריטים של לקוח.
            </Box>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr">
                  {headersFor(HEADERS, units).map((h, i) => (
                    <Box
                      component="th"
                      key={`${h}-${i}`}
                      sx={HIDDEN_ON_MOBILE.has(i) ? { ...th, ...MOBILE_HIDDEN } : th}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {templates.map((template) => {
                  const totals = templateTotals(template);
                  return (
                    <Box
                      component="tr"
                      key={template.id}
                      data-testid="menu-template-row"
                      onClick={() => navigate(`/app/menu-templates/${template.id}`)}
                      sx={{ cursor: "pointer", transition: "background .12s", "&:hover": { background: v2.hover } }}
                    >
                      <Box component="td" sx={{ ...cell, fontWeight: 700, color: v2.ink, textAlign: "start" }}>
                        {template.name}
                      </Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN }}>{template.meals.length}</Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN }}>{itemCount(template)}</Box>
                      <Box component="td" sx={{ ...cell, fontWeight: 700 }}>
                        {energyCell(totals.calories, units)}
                      </Box>
                      <Box component="td" sx={{ ...cell, ...MOBILE_HIDDEN }}>
                        <Box sx={{ display: "inline-flex", gap: "5px", fontSize: 11.5, fontWeight: 600 }}>
                          <Box component="span" sx={{ color: macro.protein.text }}>{macroCell("protein", totals.protein, units)}</Box>
                          <Box component="span" sx={{ color: v2.ink3 }}>·</Box>
                          <Box component="span" sx={{ color: macro.carbs.text }}>{macroCell("carbs", totals.carbs, units)}</Box>
                          <Box component="span" sx={{ color: v2.ink3 }}>·</Box>
                          <Box component="span" sx={{ color: macro.fats.text }}>{macroCell("fats", totals.fats, units)}</Box>
                        </Box>
                      </Box>
                      <Box component="td" sx={cell} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Tooltip title="מחק" arrow>
                          <Box
                            component="button"
                            type="button"
                            aria-label={`מחק ${template.name}`}
                            onClick={() => setDeleting(template)}
                            sx={iconBtn(v2.over)}
                          >
                            <DeleteIcon sx={{ fontSize: 17 }} />
                          </Box>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default MenuTemplatesListV2;
